"use client";

import { useEffect } from "react";
import { GAM_INTERSTITIAL_AD_UNIT } from "@/components/ads/config";

/**
 * Official GPT out-of-page web interstitial, one slot per document.
 * Google owns rendering, eligibility, navigation interception and frequency caps.
 * No homemade fullscreen modal, no refresh loop and no simulated clicks.
 */
export default function GamWebInterstitial() {
  useEffect(() => {
    type Slot = {
      addService: (service: unknown) => Slot;
      setConfig?: (config: { interstitial: { triggers: Record<string, boolean> } }) => Slot;
    };
    type Tag = {
      cmd: Array<() => void>;
      enums?: { OutOfPageFormat?: { INTERSTITIAL?: unknown } };
      defineOutOfPageSlot?: (path: string, format: unknown) => Slot | null;
      pubads: () => unknown;
      enableServices: () => void;
      display: (slot: Slot) => void;
    };
    const w = window as typeof window & { __bfcGamInterstitialOnce?: boolean };
    if (w.__bfcGamInterstitialOnce) return;
    w.__bfcGamInterstitialOnce = true;

    // GPT loads asynchronously. Initialize its command queue before using it,
    // so a slow or blocked external script cannot crash the whole React app.
    const globalWindow = window as typeof window & { googletag?: Partial<Tag> };
    globalWindow.googletag = globalWindow.googletag || { cmd: [] };
    globalWindow.googletag.cmd = globalWindow.googletag.cmd || [];
    const tag = globalWindow.googletag as Tag;
    tag.cmd.push(() => {
      const format = tag.enums?.OutOfPageFormat?.INTERSTITIAL;
      if (!format || !tag.defineOutOfPageSlot || !tag.pubads || !tag.display || !tag.enableServices) {
        console.info("[BFC GAM] Native web interstitial unavailable in this browser");
        return;
      }
      const slot = tag.defineOutOfPageSlot(GAM_INTERSTITIAL_AD_UNIT, format);
      if (!slot) {
        console.info("[BFC GAM] Interstitial slot was not created");
        return;
      }
      slot.setConfig?.({
        interstitial: {
          triggers: {
            unhideWindow: false,
            navBar: false,
            inactivity: false,
            endOfArticle: false,
            continueReading: false,
            backward: false,
          },
        },
      });
      slot.addService(tag.pubads());
      tag.enableServices();
      tag.display(slot);
      console.info("[BFC GAM] Native interstitial initialized", GAM_INTERSTITIAL_AD_UNIT);
    });
    // Keep the slot alive for the lifetime of the SPA document.
  }, []);
  return null;
}
